import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, createUserEmailPassword } from "../../services/firebaseAuth";
import isEmail from "validator/lib/isEmail";
import isStrongPassword from "validator/lib/isStrongPassword";
import { Button, Grid, Typography, useTheme } from "@mui/material";
import { FirebaseError } from "firebase/app";
import CustomTextField from "../Base/CustomTextField";
import BackgroundWrapper from "../BackgroundWrapper";
import { getRedirectUrl } from "../../utils/redirectUtils";

const Signup = () => {
  const navigate = useNavigate();
  const [signUpSuccessMsg, setSignUpSuccessMsg] = useState("");

  const [user, loading] = useAuthState(auth);

  const [email, setEmail] = useState<string>("");
  const [isEmailValid, setIsEmailValid] = useState<boolean>(false);
  const [isEmailDirty, setIsEmailDirty] = useState<boolean>(false);

  const [password, setPassWord] = useState<string>("");
  const [isPasswordValid, setIsPasswordValid] = useState<boolean>(false);
  const [isPasswordDirty, setIsPasswordDirty] = useState<boolean>(false);

  const handleEmailChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const emailValue: string = e.target.value;
    setEmail(emailValue);
    setIsEmailValid(isEmail(emailValue));
  };

  const handlePasswordChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const passwordValue: string = e.target.value;
    setPassWord(passwordValue);
    setIsPasswordValid(isStrongPassword(passwordValue));
  };

  useEffect(() => {
    if (loading) return;
    if (user) {
      // Rediriger vers l'URL sauvegardée ou la page d'accueil
      navigate(getRedirectUrl());
    }
  }, [user, loading, navigate]);

  const theme = useTheme();

  return (
    <BackgroundWrapper>
      <Grid
        container
        direction="column"
        rowSpacing={4}
        justifyContent="center"
        alignItems="center"
        sx={{
          maxWidth: "600px", // Largeur maximale
          width: "100%", // Prend toute la largeur disponible
          mx: "auto", // Centré horizontalement
        }}
      >
        {/* Informative text */}
        <Grid item>
          <Typography
            variant="body1"
            sx={{
              width: "100%",
              textAlign: "center",
              bgcolor: theme.palette.secondary.main,
              color: theme.palette.text.primary,
              p: 2,
              borderRadius: 1,
            }}
          >
            Afin de pouvoir vous inscrire au tournoi ou accéder à certaine
            partie du site, veuillez créer un compte.
          </Typography>
        </Grid>
        <Grid item sx={{ width: "100%" }}>
          <CustomTextField
            label="Email"
            type="email"
            fullWidth
            required
            value={email}
            onChange={handleEmailChange}
            onBlur={() => setIsEmailDirty(true)}
            error={isEmailDirty && !isEmailValid}
            helperText={isEmailDirty && !isEmailValid && "Email invalide"}
          />
        </Grid>
        {/* Password Input */}
        <Grid item sx={{ width: "100%" }}>
          <CustomTextField
            label="Mot de passe"
            type="password"
            fullWidth
            required
            value={password}
            onChange={handlePasswordChange}
            onBlur={() => setIsPasswordDirty(true)}
            error={isPasswordDirty && !isPasswordValid}
            helperText="Le mot de passe doit contenir au moins 8 caractères, une majuscule, une minuscule, un chiffre et un caractère spécial."
          />
        </Grid>
        <Grid item>
          {signUpSuccessMsg.length > 0 && (
            <Grid marginBottom={2}>
              <Typography variant="body2" color="red">
                {signUpSuccessMsg}
              </Typography>
            </Grid>
          )}
          <Grid item>
            <Button
              variant="contained"
              disabled={!isPasswordValid || !isEmailValid}
              onClick={async () => {
                try {
                  const result = await createUserEmailPassword(email, password);
                  if (!result) {
                    setSignUpSuccessMsg(
                      "Erreur lors de la création du compte."
                    );
                  }
                } catch (error) {
                  if (
                    error instanceof FirebaseError &&
                    error.code === "auth/email-already-in-use"
                  ) {
                    setSignUpSuccessMsg("Email déjà utilisé");
                  } else {
                    setSignUpSuccessMsg(
                      "Erreur lors de la création du compte."
                    );
                  }
                }
              }}
              sx={{
                width: "100%",
                py: 1.5,
                textTransform: "none",
                fontWeight: 600,
                mt: 2,
                backgroundColor: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: theme.palette.primary.dark,
                  transform: "scale(1.05)", // Agrandissement du bouton au survol
                  transition: "transform 0.3s ease-in-out", // Transition douce
                },
              }}
            >
              Créer un compte
            </Button>
          </Grid>
        </Grid>

        <Grid item container justifyContent="center" sx={{ mt: 2 }}>
          <Grid item>
            <Link
              to="/login"
              style={{
                textDecoration: "none",
                color: theme.palette.primary.main,
                fontWeight: 500,
              }}
            >
              Déjà un compte ? Connectez-vous
            </Link>
          </Grid>
        </Grid>
      </Grid>
    </BackgroundWrapper>
  );
};

export default Signup;
